import { computed, Injectable, signal } from '@angular/core';
import { Element } from '../../equipment/models/elements/element';
import { ProductGroup } from '../models/product-group';
import { HighlightOptionsValue } from '../../core/services/highlight-products.service';
import { Block } from '../models/block';
import { Utils } from '../../core/services/utils.service';
import { IPlanogram } from '../models/planogram';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { POSMaterial } from '../models/POSMaterials/POSMaterial';
import { LayoutBlock } from '../planogram-edit/components/layout-rules/layout-rules.component';
import { ShelfElement } from '../models/elements/shelfElement';
import { PalletElement } from '../models/elements/palletElement';
import { EquipmentBase } from '../models/equipment-base';
import { HangBarElement } from '../models/elements/hangBarElement';
import { Product } from '../models/product';
import { ExtensionType } from '../../equipment/models/elements/extension';
import { DndDropEvent } from 'ngx-drag-drop';
import { PlanogramViewBehavior } from '../models/planogramViewBehavior';
import { Point } from '../../core/models/point';
import { ICanPutProductsResult } from '../models/canPutProductsResult';
import { BoxElement } from '../models/elements/boxElement';
import { SessionService } from '../../core/services/session.service';
import { SnackBarService } from '../../core/services/snack-bar.service';
import { filter, firstValueFrom, Subject, take } from 'rxjs';
import { HookElement } from '../models/elements/hookElement';
import { HoleElement } from '../models/elements/holeElement';
import { AlignmentTypes } from '../models/alignment';

export const NORMALIZED_ELEMENTS = [
	'ShelfElement',
	'HangBarElement',
	'BoxElement',
	'HookElement',
	'PalletElement',
	'HoleElement'
];

const CAN_CHANGE_PRODUCT_POSITION: Array<EquipmentElements> = [
	EquipmentElements.BaseRectangle,
	EquipmentElements.BaseTrapeze,
	EquipmentElements.Shelf,
	EquipmentElements.AngleShelf,
	EquipmentElements.Box,
	EquipmentElements.SlicedBox,
	EquipmentElements.Hook,
	EquipmentElements.Hole
];

export interface ILabelsState {
	abcReceipts: boolean;
	abcTurnover: boolean;
	dateFrom: string;
	dateTo: string;
	price: boolean;
	promoProducts: boolean;
	newProducts: boolean;
	articles: boolean;
	blocks: boolean;
	stockQty: boolean;
	allShops: boolean;
}

export interface DetailedVariables {
	detailedElementNumber: number;
	detailedElementNext: Element;
	detailedElementPrev: Element;
}

export interface CanvasUpdateRequest {
	stands?: Array<IPlanogram>;
	ids?: Array<string>;
	reason?: string;
	requestId?: string;
}

interface ISettingsPanel {
	emptyProducts: boolean;
	emptyBlocks: boolean;
	emptyLabels: boolean;
	tooManyLabels: boolean;
	differentElements: boolean;
	looseProduct: boolean;
}

@Injectable()
export class PlanogramActivity {

	private _planogram: IPlanogram = null;

	get planogram(): IPlanogram {
		return this._planogram;
	}

	set planogram(value: IPlanogram) {
		this._planogram = value;
	}

	private readonly _activeBlocks = signal<Array<Block>>([]);
	private readonly _activePosMaterials = signal<Array<POSMaterial>>([]);
	private readonly _activePanelProducts = signal<Array<Product>>([]);
	private readonly _activeProducts = signal<Array<ProductGroup>>([]);
	private readonly _dragProducts = signal<Array<Product>>([]);
	private readonly _editMode = signal<boolean>(false);
	private readonly _filters = signal<{ interval: number, metrics: Array<string> }>({ interval: 1, metrics: [] });
	private readonly _filterType = signal<string>('default');
	private readonly _focusedCanvas = signal<{ planogram: number, element: string, canvas: HTMLDivElement }>({ planogram: null, element: null, canvas: null });
	private readonly _highlightValue = signal<HighlightOptionsValue>(HighlightOptionsValue.Empty);
	private readonly _highlightedProducts = signal<{ [key: number]: any }>({});
	private readonly _labelsState = signal<ILabelsState>({
		stockQty: false,
		price: false,
		promoProducts: false,
		newProducts: false,
		articles: false,
		blocks: true,
		abcReceipts: false,
		abcTurnover: false,
		allShops: false,
		dateTo: null,
		dateFrom: null
	});
	private readonly _layoutRulesProducts = signal<any>({});
	private readonly _panelType = signal<string>('');
	private readonly _productMove = signal<boolean>(false);
	private readonly _rotatedProducts = signal<{ [key: number]: { color: string } }>({});
	private readonly _selectedBlock = signal<Block>(null);
	private readonly _selectedElement = signal<string>('');
	private readonly _settingsPanel = signal<ISettingsPanel>({
		emptyProducts: true,
		emptyBlocks: true,
		emptyLabels: true,
		tooManyLabels: false,
		differentElements: false,
		looseProduct: false
	});
	private readonly _standData = signal<{ [key: number]: any }>({});
	private readonly _targetElement = signal<Element | null>(null);

	public readonly activeBlocks = this._activeBlocks.asReadonly();
	public readonly activePosMaterials = this._activePosMaterials.asReadonly();
	public readonly activePanelProducts = this._activePanelProducts.asReadonly();
	public readonly activeProducts = this._activeProducts.asReadonly();
	public readonly blockForRule = computed(() => {
		const block: Block = this._activeBlocks()[0] ?? this._selectedBlock();
		const blockForRule: LayoutBlock = {
			id: null,
			start: 0,
			end: this.planogram.stand.Width,
			shelves: [],
			width: this.planogram.stand.Width
		};

		if (block) {
			blockForRule.id = block.localId;
			blockForRule.start = Math.max(block.begin[0].x, 0);
			blockForRule.end = Math.min(Utils.round(block.begin[0].x + block.width), block.stand.Width);
			blockForRule.shelves = Utils.unique(
				block.elements
					.filter((el: Element) => el.is(ShelfElement, PalletElement))
					.map((el: Element) => Utils.getElementIndex(el, block.stand).toString())
			);
			blockForRule.width = Utils.round(blockForRule.end - blockForRule.start);
		}

		if (!Utils.equalObjects(blockForRule, this.blockForRule)) return blockForRule;
	});
	public readonly canChangeProductPosition = computed(() => {
		const activeProducts: Array<ProductGroup> = this._activeProducts();
		return activeProducts.length == 1 && activeProducts[0].element.parent.is(...CAN_CHANGE_PRODUCT_POSITION) && !activeProducts[0].isLooseForm;
	});
	public readonly dragProducts = this._dragProducts.asReadonly();
	public readonly editMode = this._editMode.asReadonly();
	public readonly filters = this._filters.asReadonly();
	public readonly filterType = this._filterType.asReadonly();
	public readonly focusedCanvas = this._focusedCanvas.asReadonly();
	public readonly highlightValue = this._highlightValue.asReadonly();
	public readonly highlightActive = computed(() => this._highlightValue() != HighlightOptionsValue.Empty);
	public readonly highlightedProducts = this._highlightedProducts.asReadonly();
	public readonly editLabelsActive = computed(() => {
		const state: ILabelsState = this._labelsState();
		return state.abcTurnover || state.abcReceipts || state.price || state.stockQty;
	});
	public readonly viewLabelsActive = computed(() => {
		const state: ILabelsState = this._labelsState();
		return state.price || state.promoProducts || state.newProducts || state.articles || state.blocks;
	});
	public readonly labelsState = this._labelsState.asReadonly();
	public readonly layoutRulesProducts = this._layoutRulesProducts.asReadonly();
	public readonly panelBlock = computed(() => this._activeBlocks().length ? this._activeBlocks()[0].copy() : null);
	public readonly panelOpened = computed(() => this._panelType() != '');
	public readonly panelPOSMaterial = computed(() => this._activePosMaterials()[0] ?? null);
	public readonly panelType = this._panelType.asReadonly();
	public readonly previewProduct = computed(() => {
		return this._activePanelProducts()?.length === 1 ? this._activePanelProducts()[0] : null;
	});
	public readonly previewProductStatus = computed(() => this.previewProduct() ? 'active' : 'inactive');
	public readonly productMove = this._productMove.asReadonly();
	public readonly rotatedProducts = this._rotatedProducts.asReadonly();
	public readonly selectedBlock = this._selectedBlock.asReadonly();
	public readonly selectedElement = this._selectedElement.asReadonly();
	public readonly settingsPanel = this._settingsPanel.asReadonly();
	public readonly settingsPanelDisabled = computed(() => {
		const settings: ISettingsPanel = this._settingsPanel();
		const empty: boolean = settings.emptyProducts && settings.emptyBlocks && settings.emptyLabels;
		return empty || settings.differentElements || settings.looseProduct || settings.tooManyLabels;
	});
	public readonly sortingActive = computed(() => {
		if (this._activeProducts().length > 1) {
			const groupedByElement = Utils.groupBy(this._activeProducts(), 'element');

			if (groupedByElement.length > 1) {
				return false;
			} else {
				if (groupedByElement[0][0].element.parent.is(EquipmentElements.Hook, EquipmentElements.Hole)) return false;
				const maxX = Math.max(...this._activeProducts().map((pg: ProductGroup) => pg.getBoundingX().max));
				const minX = Math.min(...this._activeProducts().map((pg: ProductGroup) => pg.begin.x));

				return !this.planogram.stand.products
					.filter((pg: ProductGroup) => pg.element == groupedByElement[0][0].element)
					.some((pg: ProductGroup) => pg.begin.x < maxX && pg.begin.x > minX && !this.isProductActive(pg));
			}
		} else if (this._activeBlocks().length) {
			return this._activeBlocks()[0].elements.some((el: Element) => el.parent.is(EquipmentElements.Hook, EquipmentElements.Hole));
		} else {
			return false;
		}
	});
	public readonly standData = this._standData.asReadonly();
	public readonly targetElement = this._targetElement.asReadonly();

	public buildProductsTable$: Subject<boolean> = new Subject<boolean>();
	public historyGenerate$: Subject<void> = new Subject<void>();
	public planogramUpdate$: Subject<IPlanogram> = new Subject<IPlanogram>();
	public rebuildProductsTable$: Subject<void> = new Subject<void>();
	public reloadABCReport$: Subject<void> = new Subject<void>();
	public reloadEfficiencyAnalysis$: Subject<void> = new Subject<void>();
	public reloadRealProductsPercents$: Subject<void> = new Subject<void>();
	public setNumbersForPerforation$: Subject<Array<Element>> = new Subject<Array<Element>>();
	public showDeleteMessage$: Subject<{ block: Block, errors: Array<ProductGroup> }> = new Subject<{ block: Block, errors: Array<ProductGroup> }>();
	public canvasUpdate$: Subject<CanvasUpdateRequest> = new Subject<CanvasUpdateRequest>();
	public canvasUpdateCompleted$: Subject<CanvasUpdateRequest> = new Subject<CanvasUpdateRequest>();

	constructor(private sessionService: SessionService, private snackBarService: SnackBarService) {}

	public addActiveBlock(block: Block): void {
		this._activeBlocks.update((blocks: Array<Block>) => [...blocks, block]);
	}

	public isBlockActive(block: Block): boolean {
		return this._activeBlocks().includes(block);
	}

	public setActiveBlocks(blocks: Array<Block>): void {
		this._activeBlocks.set(blocks);
	}

	public refreshActiveBlocks(): void {
		this._activeBlocks.update((blocks: Array<Block>) => [...blocks]);
	}

	public clearActiveBlocks(): void {
		this._activeBlocks.set([]);
	}

	public addActivePOSMaterial(material: POSMaterial): void {
		this._activePosMaterials.update((materials: Array<POSMaterial>) => [...materials, material]);
	}

	public removeActivePOSMaterial(material: POSMaterial): void {
		this._activePosMaterials.update((materials: Array<POSMaterial>) => materials.filter((m: POSMaterial) => m !== material));
	}

	public isPOSMaterialActive(material: POSMaterial): boolean {
		return this._activePosMaterials().includes(material);
	}

	public setActivePOSMaterials(materials: Array<POSMaterial>): void {
		this._activePosMaterials.set(materials);
	}

	public refreshPOSMaterials(): void {
		this._activePosMaterials.update((materials: Array<POSMaterial>) => [...materials]);
	}

	public clearPOSMaterials(): void {
		this._activePosMaterials.set([]);
	}

	public addActiveProduct(pg: ProductGroup): void {
		this._activeProducts.update((products: Array<ProductGroup>) => [...products, pg]);
	}

	public removeActiveProduct(pg: ProductGroup): void {
		this._activeProducts.update((products: Array<ProductGroup>) => products.filter((p: ProductGroup) => p != pg));
	}

	public isProductActive(pg: ProductGroup): boolean {
		return this._activeProducts().includes(pg);
	}

	public setActiveProducts(groups: Array<ProductGroup>): void {
		this._activeProducts.set(groups);
	}

	public refreshActiveProducts(): void {
		this._activeProducts.update((products: Array<ProductGroup>) => [...products]);
	}

	public clearActiveProducts(): void {
		this._activeProducts.set([]);
	}

	public addActivePanelProduct(product: Product): void {
		product.selected = true;
		this._activePanelProducts.update((products: Array<Product>) => [...products, product]);
	}

	public removeActivePanelProduct(product: Product): void {
		product.selected = false;
		this._activePanelProducts.update((products: Array<Product>) => products.filter((p: Product) => p != product));
	}

	public isPanelProductActive(product: Product): boolean {
		return this._activePanelProducts().includes(product);
	}

	public setActivePanelProducts(products: Array<Product>): void {
		products.forEach((product: Product) => product.selected = true);
		this._activePanelProducts.set(products);
	}

	public refreshActivePanelProducts(): void {
		this._activePanelProducts.update((products: Array<Product>) => [...products]);
	}

	public clearActivePanelProducts(): void {
		this._activePanelProducts().forEach((product: Product) => product.selected = false);
		this._activePanelProducts.set([]);
	}

	public setDragProducts(products: Array<Product>): void {
		this._dragProducts.set(products);
	}

	public clearDragProducts(): void {
		this._dragProducts.set([]);
	}

	public setEditMode(value: boolean): void {
		this._editMode.set(value);
	}

	public setFilters(value: { interval: number, metrics: Array<string> }): void {
		this._filters.set(value);
	}

	public setFilterType(type: string): void {
		this._filterType.set(type);
	}

	public setFocusedCanvas(planogram: number, element: string, canvas: HTMLDivElement): void {
		this._focusedCanvas.set({ planogram: planogram, element: element, canvas: canvas });
	}

	public isCanvasFocused(canvas: HTMLDivElement): boolean {
		return this._focusedCanvas().canvas == canvas;
	}

	public setHighlightValue(value: HighlightOptionsValue): void {
		this._highlightValue.set(value);
	}

	public clearHighlightValue(): void {
		this._highlightValue.set(HighlightOptionsValue.Empty);
	}

	public setHighlightedProducts(value: any): void {
		this._highlightedProducts.set(value);
	}

	public isProductHighlighted(id: number): boolean {
		return this._highlightedProducts().hasOwnProperty(id);
	}

	public clearHighlightedProducts(): void {
		this._highlightedProducts.set({});
	}

	public updateLabelsState(values: Partial<ILabelsState>): void {
		this._labelsState.update((state: ILabelsState) => ({ ...state, ...values }));
	}

	public setLayoutRulesProducts(value: any): void {
		this._layoutRulesProducts.set(value);
	}

	public isProductInRule(id: number): boolean {
		return this._layoutRulesProducts().hasOwnProperty(id);
	}

	public clearLayoutRulesProducts(): void {
		this._layoutRulesProducts.set({});
	}

	public openPanel(type: string): void {
		this._panelType.set(type);
	}

	public closePanel(): void {
		this._panelType.set('');
	}

	public setProductMove(state: boolean): void {
		this._productMove.set(state);
	}

	/*public startProductMove(): void {
		this._productMove.set(true);
	}

	public stopProductMove(): void {
		this._productMove.set(false);
	}*/

	public addRotatedProduct(id: number): void {
		this._rotatedProducts.update((products) => ({ ...products, [id]: { color: 'green' } }));
	}

	public isProductRotated(id: number): boolean {
		return this._rotatedProducts().hasOwnProperty(id);
	}

	public setSelectedElement(value: string): void {
		this._selectedElement.set(value);
	}

	public clearSelectedElement(): void {
		this._selectedElement.set('');
	}

	public setSelectedBlock(block: Block): void {
		this._selectedBlock.set(block);
	}

	public clearSelectedBlock(): void {
		this._selectedBlock.set(null);
	}

	public updateSettingsPanel(values: Partial<ISettingsPanel>): void {
		this._settingsPanel.update((settings: ISettingsPanel) => ({ ...settings, ...values }));
	}

	public getStandData(id: number): any {
		return this._standData()[id] ?? null;
	}

	public setStandData(data: any) {
		this._standData.set(data);
	}

	public updateStandData(id: number, data: any) {
		this._standData.update((items: any) => ({ ...items, [id]: data }));
	}

	public clearStandData(): void {
		this._standData.set({});
	}

	public setTargetElement(target: Element): void {
		this._targetElement.set(target);
	}

	public clearTargetElement(): void {
		this._targetElement.set(null);
	}

	public requestCanvasUpdate(stands: Array<IPlanogram> = [], ids: Array<string> = [], reason?: string): void {
		this.canvasUpdate$.next({ stands, ids, reason });
	}

	public requestCanvasUpdateAsync(stands: Array<IPlanogram> = [], ids: Array<string> = [], reason?: string): Promise<CanvasUpdateRequest> {
		const requestId = `${Date.now()}-${Math.random()}`;
		const payload: CanvasUpdateRequest = { stands, ids, reason, requestId };

		this.canvasUpdate$.next(payload);

		return firstValueFrom(
			this.canvasUpdateCompleted$.pipe(
				filter((event: CanvasUpdateRequest) => event.requestId === requestId),
				take(1)
			)
		);
	}

	public syncProducts(pg: ProductGroup, event: MouseEvent): void {
		if (navigator.platform == 'MacIntel' ? event?.metaKey : event?.ctrlKey) {
			if (!this.isPanelProductActive(pg.product)) {
				this.addActivePanelProduct(pg.product);
			} else if (this.activePanelProducts().length > 1) {
				this.removeActivePanelProduct(pg.product);
			}
		} else {
			this.clearActivePanelProducts();
			this.addActivePanelProduct(pg.product);
		}
	}

	public setProductActive(product: ProductGroup = null, multi: boolean = false, checkInactivity: boolean = true): void {
		if (this._activeBlocks().length) this.setBlockActive(null, false);
		if (this._activePosMaterials().length) this.setPOSMaterialActive();

		if (!product || !multi) this.clearActiveProducts();

		if (product) {
			if (!this.isProductActive(product)) {
				this.addActiveProduct(product);
			} else if (this._activeProducts().length > 1) {
				this.removeActiveProduct(product);
			}
		}

		this.updateSettingsPanel({
			emptyProducts: !this._activeProducts().length,
			differentElements: Utils.unique(this._activeProducts().map((pg) => pg.element.constructor.name)).length > 1,
			looseProduct: this._activeProducts().some((p: ProductGroup) => p.isLooseForm) && this._activeProducts().some((p: ProductGroup) => !p.isLooseForm)
		});

		if (checkInactivity && this.settingsPanelDisabled()) this.closePanel();
	}

	public setBlockActive(element: Block = null, checkInactivity: boolean = true) {
		if (this.activeProducts().length) this.setProductActive(null, false, false);
		if (this.activePosMaterials().length) this.setPOSMaterialActive();
		if (!element && this.activeBlocks().length) this.closePanel();
		this.setActiveBlocks(element ? [element] : []);
		this.updateSettingsPanel({ emptyBlocks: !this.activeBlocks().length });
		if (checkInactivity && this.settingsPanelDisabled()) this.closePanel();
	}

	public setPOSMaterialActive(material: POSMaterial = null, multi: boolean = false): void {
		if (this.activeBlocks().length) this.setBlockActive();
		if (this.activeProducts().length) this.setProductActive();

		if (!material) {
			this.clearPOSMaterials();
			this.updateSettingsPanel({ emptyLabels: true, tooManyLabels: false });
			return;
		}

		if (multi) {
			if (this.isPOSMaterialActive(material)) {
				this.removeActivePOSMaterial(material);
			} else {
				this.addActivePOSMaterial(material);
			}
		} else {
			this.setActivePOSMaterials([material]);
		}

		this.updateSettingsPanel({
			emptyLabels: !this.activePosMaterials().length,
			tooManyLabels: this.activePosMaterials().length > 1
		});

		if (this.settingsPanelDisabled()) this.closePanel();
	}

	public normalizeElements(stand: EquipmentBase, elements: Array<Element> = [], segments: boolean = false): Array<Element> {
		const alreadyNormalized: Array<Element> = [];
		if (!elements.length || this.highlightValue() === HighlightOptionsValue.Doubles) elements = stand.allElements;
		elements.forEach((el: Element) => {
			if (NORMALIZED_ELEMENTS.includes(el.constructor.name) && alreadyNormalized.every((n) => n != el)) {
				el.normalize(stand, segments ? (this.blockForRule().id ? el.getBlockSegments : el.getBlockNotEmptySegments) : []);
				alreadyNormalized.push(el, ...el.getSiblingElements);
			}
		});
		stand.products
			.filter((pg: ProductGroup) => alreadyNormalized.includes(pg.element))
			.forEach((pg: ProductGroup) => delete pg.block);
		stand.blocks
			.sort((a: Block, b: Block) => {
				if (stand.alignment === 'right') {
					return b.boundingX.min - a.boundingX.min;
				} else {
					return a.boundingX.max - b.boundingX.max;
				}
			})
			.forEach((block: Block, idx: number) => {
				const otherBlockProducts = Utils.to1DArray(
						stand.blocks.filter((b: Block, i: number) => i < idx).map((b: Block) => b.products)
					),
					blockProductErrors = block.setProducts(otherBlockProducts);

				if (blockProductErrors.length) {
					this.showDeleteMessage$.next({ block: block, errors: blockProductErrors });
				}
			});

		return alreadyNormalized;
	}

	public changeProductPosition(pg: ProductGroup): void {
		if (pg.element.is(HangBarElement) || pg.isLooseForm) return;

		//this.changeCanvasListening(false);
		const dependedProduct: ProductGroup = this.planogram.stand.products
			.find((p: ProductGroup) => p.dependProduct == pg);

		pg.rotate();

		const pIdx: number = this.planogram.stand.products.indexOf(pg),
			aIdx: number = this.activeProducts().indexOf(pg),
			pgCopy: ProductGroup = pg.copy();

		this.planogram.stand.products[pIdx] = pgCopy;
		this.activeProducts()[aIdx] = pgCopy;

		if (dependedProduct) dependedProduct.dependProduct = pgCopy;

		let dependedProducts: Array<ProductGroup> = Utils.findDependProducts(pg, this.planogram.stand.products);

		this.planogram.stand.products
			.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingY().min - b.getBoundingY().min)
			.forEach((p: ProductGroup) => {
				if (dependedProducts.includes(p)) {
					p.begin.y = p.dependProduct ? p.dependProduct.getBoundingY().max : p.element.getBoundingY().max;
				}
			});

		const groupToShift: Array<Array<ProductGroup>> = pgCopy.element.products
			.filter((group: Array<ProductGroup>) => group.some((p: ProductGroup) => pgCopy == p));

		pgCopy.element.shiftProductGroups(groupToShift, Utils.to1DArray(groupToShift));

		const elements: Array<Element> = this.normalizeElements(this.planogram.stand, [pgCopy.element]);

		this.planogram.stand.blocks.forEach((b: Block) => {
			const blockProductErrors: Array<ProductGroup> = b.setProducts(
				Utils.to1DArray(
					this.planogram.stand.blocks
						.filter((block: Block) => block != b)
						.map((block: Block) => block.products)
				)
			);

			if (blockProductErrors.length) this.showDeleteMessage$.next({ block: b, errors: blockProductErrors });
		});

		/*this.urgentUpdateCanvas([this.planogram], elements.map((el: Element) => el.id))
			.finally(() => {
				this.stopProductMove(true);
			});*/
		this.requestCanvasUpdate([], elements.map((el: Element) => el.id));
		this.historyGenerate$.next();
		this.reloadRealProductsPercents$.next();
	}

	public getDependBlockProducts(block: Block) {
		let dependProducts = [];
		const groupedProducts = Utils.groupByChildProperty(block.products, 'begin', 'x');
		groupedProducts.forEach((g) => {
			g.sort((a, b) => a.getBoundingY().min - b.getBoundingY().min);
			dependProducts.push(
				...this.planogram.stand.products.filter(
					(p) => p.begin.x == g[0].begin.x && p.element == g[0].element
				)
			);
		});

		return [...block.products, ...dependProducts.filter((p) => !block.products.includes(p))];
	}

	public getDetailedVariables(detailedElement: Element): DetailedVariables {
		const basesList: Array<EquipmentElements> = [EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze];
		const shelvesList: Array<EquipmentElements> = [EquipmentElements.Shelf, EquipmentElements.AngleShelf];
		const backWallsList: Array<EquipmentElements> = [
			EquipmentElements.BackWall,
			EquipmentElements.CentralPillar,
			EquipmentElements.HookWall,
			EquipmentElements.PerforatedWall,
			EquipmentElements.Showcase
		];

		if (detailedElement.parent.is(...basesList, ...shelvesList)) {
			const sortedShelves: Array<Element> = [];

			if (detailedElement.parent.is(...basesList)) {
				const backWall: Element = detailedElement.stand.allElements.find((el: Element) => {
					return backWallsList.includes(el.parent.type) && el.depend.id === detailedElement.id;
				});

				sortedShelves.push(
					detailedElement,
					...detailedElement.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend?.id === backWall?.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			if (detailedElement.parent.is(...shelvesList)) {
				const backWall: Element = detailedElement.depend;
				const base: Element = backWall.depend;

				sortedShelves.push(
					base,
					...detailedElement.stand.allElements
						.filter((el: Element) => shelvesList.includes(el.parent.type) && el.depend.id === backWall.id)
						.sort((a: Element, b: Element) => {
							const ySort: number = Utils.round(a.shapes[0].begin.y) - Utils.round(b.shapes[0].begin.y);
							const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

							return ySort || xSort;
						})
				);
			}

			const availableShelves: Array<Element> = sortedShelves.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = sortedShelves.findIndex((el: Element) => el === detailedElement);

			return {
				detailedElementNumber: availableShelves.findIndex((el: Element) => el === detailedElement) + 1,
				detailedElementNext: currentIndex + 1 < sortedShelves.length ? sortedShelves[currentIndex + 1] : null,
				detailedElementPrev: currentIndex - 1 >= 0 ? sortedShelves[currentIndex - 1] : null
			};
		}

		if (detailedElement.parent.is(EquipmentElements.Hook)) {
			const parent: Element = detailedElement.depend;
			const hooks: Array<Element> = detailedElement.stand.allElements
				.filter((el: Element) => el.depend?.id === parent.id)
				.sort((a: Element, b: Element) => {
					const ySort: number = Utils.round(b.shapes[0].begin.y) - Utils.round(a.shapes[0].begin.y);
					const xSort: number = Utils.round(a.shapes[0].begin.x) - Utils.round(b.shapes[0].begin.x);

					return ySort || xSort;
				});

			const availableHooks: Array<Element> = hooks.filter((el: Element) => {
				return !el.hasExtension(ExtensionType.Availability) || el.isExtensionActive(ExtensionType.Availability);
			});

			const currentIndex: number = hooks.findIndex((el: Element) => el === detailedElement);

			return {
				detailedElementNumber: availableHooks.findIndex((el: Element) => el === detailedElement) + 1,
				detailedElementNext: currentIndex + 1 < hooks.length ? hooks[currentIndex + 1] : null,
				detailedElementPrev: currentIndex - 1 >= 0 ? hooks[currentIndex - 1] : null
			}
		}

		return { detailedElementNumber: 0, detailedElementNext: null, detailedElementPrev: null };
	}

	public dropProduct(planogram: IPlanogram, behavior: PlanogramViewBehavior, event: DndDropEvent): void {
		if (!this.dragProducts().length) return;

		this.clearActivePanelProducts();
		const defaultElement: Element = new ShelfElement(planogram.stand.bases[0]); // TODO replace with singleton NullElement
		defaultElement.stand = planogram.stand;
		const newProductGroups: Array<ProductGroup> = this.dragProducts().map((product: Product) => {
			const selectPacking = product.is_loose && product.packings?.length ? product.packings[0].id : null;
			return new ProductGroup({
				element: defaultElement,
				product: product,
				begin: { x: 0, y: 0, z: 0 },
				qty: 1,
				selectPacking: selectPacking,
				detailedRotationPoint: this.sessionService.personalSettings.productRotationPoint
			});
		});
		const point: Point = behavior.convertCanvasPointToPlanogram({ x: event.event.offsetX, y: event.event.offsetY });
		const element: Element = behavior.getAnchorElement(newProductGroups[0], point);

		if (!element) return;

		newProductGroups.forEach((pg: ProductGroup) => pg.angle = behavior.getProductAngle(element, point));

		point.z = behavior.getCorrectZCoordinate(element, point.z, newProductGroups[0]);
		const result: ICanPutProductsResult = element.canPutProductGroups(newProductGroups, point);
		if (!result.success) {
			if (result.message) this.snackBarService.setSnackBarNotification(result.message);
			return;
		}

		const normalizeElements: Array<Element> = [element];

		// set different x point for plane elements
		if (!element.parent.is(EquipmentElements.Hook, EquipmentElements.Hole)) {
			newProductGroups.forEach((pg: ProductGroup, index: number) => {
				pg.begin.x = index ? newProductGroups[index - 1].getBoundingX().max : point.x;
			});
		}

		newProductGroups.forEach((pg: ProductGroup, index: number) => {
			if (element.is(BoxElement) && pg.product.is_loose) pg.selectPacking = null;

			element.putProductGroups([pg], newProductGroups.map((pg: ProductGroup) => [pg]), { ...point, x: pg.begin.x });

			// set placement depth according to the personal settings
			if (behavior.isDetailedBehavior || pg.element.is(BoxElement, HookElement, HoleElement)) {
				if (!behavior.isDetailedBehavior && pg.element.is(HookElement)) {
					const personalSettingsDepth: number = this.sessionService.personalSettings.productDepthHook;
					const maxAvailableCount: number = Math.floor(Utils.round(pg.zMax / pg.ItemDepth)) || 1;

					if (!!~personalSettingsDepth) {
						pg.placementDepth = Math.min(personalSettingsDepth, maxAvailableCount);
					} else {
						pg.placementDepth = maxAvailableCount;
					}
				} else {
					pg.placementDepth = 1;
				}
			} else if (planogram.stand.arbitraryLayout) {
				pg.placementDepth = 1;
			} else {
				const personalSettingsDepth: number = this.sessionService.personalSettings.productDepthShelf,
					maxAvailableCount: number = Math.floor(Utils.round(pg.zMax / pg.ItemDepth)) || 1;

				if (!!~personalSettingsDepth) {
					pg.placementDepth = Math.min(personalSettingsDepth, maxAvailableCount);
				} else {
					pg.placementDepth = maxAvailableCount;
				}
			}

			if (pg.dependProduct) {
				pg.placementDepth = Math.floor(Utils.round(pg.dependProduct.Depth / pg.ItemDepth)) || 1;
			}

			pg.qty = pg.placementDepth;

			// mark products as selected on left panel
			if (!this.isPanelProductActive(pg.product)) this.addActivePanelProduct(pg.product);

			this.setProductActive(pg, index > 0);
		});

		let block: Block = planogram.stand.findBlock(point.x, point.y, newProductGroups[0].Width);

		if (this.selectedBlock() != block) this.setSelectedBlock(block);

		normalizeElements.push(...newProductGroups.map((pg: ProductGroup) => pg.element));
		planogram.stand.shiftProducts(newProductGroups.map((pg: ProductGroup) => [pg]), behavior.isDetailedBehavior);

		this.reloadRealProductsPercents$.next();

		const elements: Array<Element> = this.normalizeElements(planogram.stand, normalizeElements);
		this.setNumbersForPerforation$.next(elements);

		this.clearDragProducts();

		this.reloadEfficiencyAnalysis$.next();
		this.reloadABCReport$.next();
		this.requestCanvasUpdate([], elements.map((el: Element) => el.id));
		this.historyGenerate$.next();
		this.buildProductsTable$.next(false);
	}

	public moveProducts(planogram: IPlanogram, behavior: PlanogramViewBehavior, horizontal: number, vertical: number): void {
		if (!this.activeProducts().length) return;

		if (planogram.stand.alignment != AlignmentTypes.Default) return;

		const movementPerformed: boolean = behavior.moveProducts(this.activeProducts(), horizontal, vertical);

		if (movementPerformed) {
			const normalizedElements: Array<Element> = this.normalizeElements(
				planogram.stand,
				Utils.unique(this.activeProducts().map((pg: ProductGroup) => pg.element))
			);

			this.historyGenerate$.next();
			this.requestCanvasUpdate([planogram], normalizedElements.map((el: Element) => el.id));
		}
	}
}